/* start - flights_split_bounds_us */
import { useClientFetch } from "../hooks/useClientFetch";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "../hooks/useReloadIfSearchStoreMissing";
import { useSearchAPIParams } from "../hooks/useSearchAPIParams";
import React, { Profiler, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSearch } from "../store/search/selectors";
import { actions as LoaderActions } from "../store/loadingScreen/actions";
import { actions as SearchResultsBoundsActions } from "../store/searchResultsBounds/actions";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions";
import { Route, useLocation, useParams } from "react-router-dom";
import useRouteName from "../hooks/useRouteName";
import StaticContext from "../app/StaticContext/StaticContext";
import useClientMetrics from "../hooks/useClientMetrics";
import { PageHeader } from "./Search";
import { AppShellDesktop } from "../components/elements/AppShell";
import Container from "../components/elements/Container";
import { Box, HiddenVisually, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useSearchbox from "../hooks/useSearchbox";
import useLocaleContext from "../hooks/useLocaleContext";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import { UISearchResults } from "@flights/types";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl";
import { trackWithDefaultStage } from "@flights/et-universal";
import { getSearchCriteria } from "../store/getSearchCriteria";
import { getSearchFetchStatus } from "../store/searchResultsBounds/inboundSelectors";
import SearchLoadingScreen from "../components/elements/LoadingScreen/components/SearchLoadingScreen";
import SearchResultsInboundDesktop from "../components/elements/SearchResults/SearchResults.inbound.desktop";
import { FLIGHT_DETAILS_INBOUND_ROUTE_PATH } from "@flights/constants";
import { HomeDesktop, FlightDetailsDesktop, SearchOutboundDesktop } from "../app/routes";
import type { ServerInitialStateFunction } from "../app/routes";
import useMeasureSearchToFlightDetails from "../hooks/useMeasureSearchToFlightDetails";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import useTrackPageLoad from "../hooks/useTrackPageLoad";
import { getVisible } from "../store/loadingScreen/selectors";
import { useWinterDeals } from "../hooks/useWinterDeals";
import { SearchInitialStateOptions } from "./options";
import useGlobalContext from "../hooks/useGlobalContext";

const SearchInboundDesktop = ({
  getInitialState
}: {
  getInitialState?: ServerInitialStateFunction<SearchInitialStateOptions>;
}) => {
  const search = useSelector(getSearch);
  const fetchStatus = useSelector(getSearchFetchStatus);
  const loadingScreenVisible = useSelector(getVisible);
  const dispatch = useDispatch();

  const location = useLocation<{ hideLoader: boolean; forceRefetch: boolean }>();
  const routeName = useRouteName();
  const staticContext = useContext(StaticContext);
  const i18n = useI18n();

  const [lastTrackedPageLoad, setLastTrackedPageLoad] = useState<
    "search_results_inbound" | "search_results_skeleton" | "search_results_modal" | ""
  >("");
  const { trackRenderTime } = useClientMetrics();
  const trackSRContextEventV2 = useTrackPageLoad("search_results_inbound");
  const trackSRSkeletonContextEventV2 = useTrackPageLoad("search_results_skeleton");
  const trackSRModalContextEventV2 = useTrackPageLoad("search_results_modal");
  const { onFetchedResults, onSelectFlight } = useMeasureSearchToFlightDetails();
  const { isWinterDealsExp } = useWinterDeals();
  const { currencyCode } = useGlobalContext();

  const queryParams = new URLSearchParams(location.search);
  // This is the server side initial state from server/screens during SSR
  if (getInitialState && staticContext.set) {
    staticContext.set({
      getInitialState: getInitialState(queryParams, { selectedBound: "inbound" })
    });
  }

  useRemoveExternalParamsFromUrl();

  // start -- FETCH DATA
  const params = useParams<{ outboundToken: string }>();
  const searchAPIParams = useSearchAPIParams({ inboundFor: params.outboundToken });
  const url = `/api/flights/?${searchAPIParams}`;
  const isMetaOrigin = getMarketingTrackingVariable("ext-origin");

  useClientFetch(url, {
    // delay the load of Search Results in background of Flight Details
    // when user lands on SR with a meta offerToken to temporarily fix an issue on ETG
    delayFetchBy: !!isMetaOrigin && routeName.includes("searchresults") ? 500 : 0,
    condition: () => {
      if (isSearchStoreInitial(search)) {
        return false;
      }

      return true;
    },
    fetch: () => {
      dispatch(SearchResultsBoundsActions.fetchInbound());
    },
    success: (data: UISearchResults) => {
      dispatch(SearchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, search)));
      dispatch(
        SearchResultsBoundsActions.fetchSuccessInbound({
          ...data,
          // We only need the first second for inbound.
          flightOffers: data.flightOffers.map((f) => ({ ...f, segments: [f.segments[1]] }))
        })
      );
      dispatch(LoaderActions.hide());
      onFetchedResults();
    },
    error: (data) => {
      dispatch(LoaderActions.hide());
      dispatch(SearchResultsBoundsActions.fetchErrorInbound(data));
    },
    forceRefetch: location?.state?.forceRefetch
  });
  // END -- fetch data

  useEffect(() => {
    if (["initial", "loading"].includes(fetchStatus)) {
      if (loadingScreenVisible && lastTrackedPageLoad !== "search_results_modal") {
        trackSRModalContextEventV2();
        setLastTrackedPageLoad("search_results_modal");
      } else if (lastTrackedPageLoad !== "search_results_skeleton") {
        trackSRSkeletonContextEventV2();
        setLastTrackedPageLoad("search_results_skeleton");
      }
    } else if (lastTrackedPageLoad !== "search_results_inbound") {
      trackSRContextEventV2();
      setLastTrackedPageLoad("search_results_inbound");
    }
  }, [fetchStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchBoxProps = useSearchbox();
  const { isRTL } = useLocaleContext();
  const searchBoxControllerProps = { ...searchBoxProps, i18n, defaultSegmentLength: search.searchSegments.length };

  const [preloadDetails, toggle] = useState(false);
  if (!preloadDetails && fetchStatus === "success") {
    toggle(true);
    SearchOutboundDesktop.preload();
    FlightDetailsDesktop.preload();
    HomeDesktop.preload();
  }

  if (useReloadIfSearchStoreMissing(search)) {
    return null;
  }

  return (
    <Profiler id="screen" onRender={trackRenderTime}>
      <PageHeader segments={search.searchSegments} />
      <AppShellDesktop>
        <Stack grow={true} gap={6}>
          <Box padding={0} backgroundColor={"neutral_alt"} mixin={{ paddingBlockStart: 6, paddingBlockEnd: 6 }}>
            <Container>
              <HiddenVisually>
                <Text tagName="h1">{i18n.trans(t("a11y_flights_sr_search_hidden_heading"))}</Text>
              </HiddenVisually>

              <Stack gap={2}>
                <SearchboxController
                  {...searchBoxControllerProps}
                  isRTL={isRTL}
                  isResponsiveFixes={!!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1)}
                  enabledCountriesAndAnywhere={true}
                  isWinterDealsExp={isWinterDealsExp}
                  fareCalendarConfig={{
                    isFareCalendarDesktopEnabled: !!trackWithDefaultStage("flights_web_cat_fare_calendar", 1),
                    currencyCode
                  }}
                >
                  {() => <SearchBoxHorizontalDefault />}
                </SearchboxController>
              </Stack>
            </Container>
          </Box>

          <Container>
            <SearchResultsInboundDesktop onSelectFlight={onSelectFlight} />
          </Container>

          <Route key={FLIGHT_DETAILS_INBOUND_ROUTE_PATH} path={FLIGHT_DETAILS_INBOUND_ROUTE_PATH} exact={true}>
            <FlightDetailsDesktop />
          </Route>

          {routeName.includes("searchresults") && <SearchLoadingScreen />}
        </Stack>
      </AppShellDesktop>
    </Profiler>
  );
};

export default SearchInboundDesktop;
/* end - flights_split_bounds_us */
